import React from "react";

import { Share } from "app/shares/interfaces";
import { AnalysisContentBlock } from "app/analysis/AnalysisContentBlock/AnalysisContentBlock";
import { CompanyDetails } from "app/shares/CompanyDetails/CompanyDetails";
import { InstrumentPerformance } from "app/performance/InstrumentPerformance/InstrumentPerformance";
import { ProfitAndLoss } from "app/shares/ProfitAndLoss/ProfitAndLoss";
import { Page, PageHeader } from "app/layout/components";
import { AnnualKeyData } from "app/shares/AnnualKeyData/AnnualKeyData";
import { NewsContentBlock } from "app/news/NewsContentBlock/NewsContentBlock";
import { NewsListItem } from "app/news/interfaces";
import { shareUrlFunction } from "app/shares/url";
import { useShareMetaData } from "app/shares/hooks";
import { ShareStageAsideCard } from "app/shares/ShareStageAsideCard/ShareStageAsideCard";
import { StageContainer } from "app/stage/StageContainer/StageContainer";
import { getHeadInfoItems } from "app/stage/functions";
import "./style.scss";
import { clientConfig } from "app/config";
import { AssetClass } from "../../../interfaces/enums";
import { TaboolaFeed } from "app/thirdParty/TaboolaFeed";

const defaultPeriod = clientConfig.defaultPeriods[AssetClass.Share];

export const ShareDetailPage = ({
    name,
    isin,
    wkn,
    freeFloat,
    category,
    mainCurrency,
    marketValue,
    company,
    profitAndLoss,
    performance,
    keyData,
    analysis,
    annualKeyData,
    news,
    marketOverview,
    equityType
}: Share) => {
    const aktie = "Aktie";

    const attributes = [`ISIN: ${isin}`, aktie, category];

    const metaData = useShareMetaData(name, aktie, isin);

    const performanceByPeriods = performance && performance.byPeriods;

    return (
        <Page
            breadcrumbFinalLabel={`${name} ${isin}`}
            metaData={metaData}
            title={`${name} ${aktie}: Aktienkurs, Chart & News (${isin})`}
            canonicalUrl={shareUrlFunction(isin, name)}
            streamListItems={marketOverview}
        >
            <PageHeader name={name} attributes={attributes} />
            <StageContainer
                chartKey={marketValue?.chartKey}
                headValue={marketValue?.lastValue}
                headCurrency={marketValue?.currency}
                headPerformance={marketValue?.changePercent}
                lastExchangeCloseValue={marketValue?.exchangeCloseValue}
                headInfoItems={getHeadInfoItems(marketValue)}
                defaultPeriod={defaultPeriod}
                performanceByPeriods={performanceByPeriods}
            >
                <ShareStageAsideCard {...keyData} forecastCurrency={mainCurrency} />
            </StageContainer>
            {company && (
                <CompanyDetails
                    name={name}
                    wkn={wkn}
                    description={company.description}
                    url={company.homepage}
                    country={company.country}
                    industry={company.industry}
                    equityType={equityType}
                    freeFloat={freeFloat}
                />
            )}
            {profitAndLoss && <ProfitAndLoss currency={mainCurrency}>{profitAndLoss}</ProfitAndLoss>}
            {annualKeyData && (
                <AnnualKeyData assetName={name} currency={mainCurrency}>
                    {annualKeyData}
                </AnnualKeyData>
            )}
            {performance && <InstrumentPerformance name={name} {...performance} />}
            {analysis && (
                <AnalysisContentBlock headlineAddon={` zur ${name} ${aktie}`} {...analysis} currency={mainCurrency} />
            )}
            {news && (
                <NewsContentBlock
                    news={news as NewsListItem[]}
                    headlineOverride={`Aktuelle Nachrichten zur ${name} Aktie`}
                />
            )}
            <TaboolaFeed />
        </Page>
    );
};
